import { CheckOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Card, Col, InputNumber, List, Row, Slider, Switch } from 'antd';
import React, { useState } from 'react';
import { groupPlansByFamily } from '../../../utils/billingUtils';
import './PricingCards.css';

/* ============================================================
   Utility — abbreviate large numbers for human readability.
   e.g. 1195345 → "~1.2M", 19345 → "~19.3K", 4945 → "~4,945"
   ============================================================ */
const abbreviateCredits = (num) => {
  if (num >= 1_000_000) {
    const val = num / 1_000_000;
    return `~${val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)}M`;
  }
  if (num >= 10_000) {
    const val = num / 1_000;
    return `~${val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)}K`;
  }
  return `~${num.toLocaleString()}`;
};

/* ============================================================
   SliderFamilyCard — renders a single plan family card with
   optional slider. "Every Button is a Buy Button" — all buttons
   are actionable, no current-plan disabling.
   ============================================================ */

const SliderFamilyCard = ({
  family,
  billingInterval,
  onSelect,
  loading,
  loadingTier,
  setLoadingTier,
  isPublicMode,
  isPublicModeLoggedIn,
  isOrgPlans,
  displaySeatCount,
  // currentTier used for slider position and "Current" badge
  currentTier,
  hasPositiveBalance,
}) => {
  const { plans, hasSlider, isPopular } = family;

  // Detect if user's current plan is in this family
  const currentPlanIndex = currentTier
    ? plans.findIndex((p) => p.tier === currentTier)
    : -1;
  const hasCurrentPlan = currentPlanIndex >= 0;

  // Slider starts at current plan position if in this family, else first stop
  // Hook must be called before any early returns (React rules of hooks)
  const [selectedIndex, setSelectedIndex] = useState(
    hasCurrentPlan ? currentPlanIndex : 0
  );

  if (plans.length === 0) return null;

  const activePlan = plans[selectedIndex] || plans[0];
  const isNitro = activePlan.tier === 'NITRO' || activePlan.tier === 'NITRO_2X';

  // --- "Every Button is a Buy Button" ---
  // No plan-options API needed. Every button is actionable.
  //
  // CTA label (Nitish, Aug 24 2026): /pricing is an acquisition surface, so it
  // reads "Get Started" for EVERYONE there, logged out and logged in alike.
  // It previously flipped to "Subscribe" the moment a visitor was signed in,
  // which made the same page speak two different languages. The wording is kept
  // EXACTLY as production already renders it (title case) — his explicit call;
  // do not "correct" it to sentence case without him.
  // In-app surfaces (billing page, upgrade overlays, welcome screen) pass
  // isPublicMode={false} and keep "Subscribe" / "Keep Credits & Subscribe" —
  // that is subscription management, not acquisition, and the "Keep Credits"
  // wording carries real reassurance for someone holding a balance.
  const getButtonText = () => {
    if (isPublicMode) return 'Get Started';
    if (loadingTier === activePlan.tier) return 'Processing...';
    return hasPositiveBalance ? 'Keep Credits & Subscribe' : 'Subscribe';
  };

  // Build slider marks — dollar amounts at each stop
  const sliderMarks = {};
  plans.forEach((plan, idx) => {
    const price = billingInterval === 'YEARLY'
      ? Math.round(plan.yearly_price / 12)
      : plan.monthly_price;
    sliderMarks[idx] = `$${price}`;
  });

  // Price display — handles both personal and org pricing
  const getPrice = () => {
    if (isOrgPlans) {
      if (billingInterval === 'YEARLY') {
        const monthlyEquiv = Math.round(activePlan.yearly_price / 12);
        return (
          <div className="pricing-card-price-label">
            <div className="price-main">${monthlyEquiv} /seat/mo</div>
            <div className="price-subtitle">billed annually (${activePlan.yearly_price} /seat/yr)</div>
          </div>
        );
      }
      return (
        <div className="pricing-card-price-label">
          <div className="price-main">${activePlan.monthly_price} /seat/mo</div>
          <div className="price-subtitle">billed monthly</div>
        </div>
      );
    }

    if (billingInterval === 'YEARLY') {
      const monthlyEquiv = Math.round(activePlan.yearly_price / 12);
      return (
        <div className="pricing-card-price-label">
          <div className="price-main">${monthlyEquiv} / Month</div>
          <div className="price-subtitle">billed annually (${activePlan.yearly_price} / year)</div>
        </div>
      );
    }
    return (
      <div className="pricing-card-price-label">
        <div className="price-main">${activePlan.monthly_price} / Month</div>
        <div className="price-subtitle">billed monthly</div>
      </div>
    );
  };

  const getSavings = () => {
    if (billingInterval === 'YEARLY') {
      const monthlyCost = activePlan.monthly_price * 12;
      const yearlyCost = activePlan.yearly_price;
      const savings = monthlyCost - yearlyCost;
      const savingsPercent = Math.round((savings / monthlyCost) * 100);
      return { amount: savings, percent: savingsPercent };
    }
    return null;
  };

  // Credits display — handles both personal and org
  const credits = billingInterval === 'YEARLY'
    ? activePlan.credits_per_month * 12
    : activePlan.credits_per_month;

  const creditsLabel = isOrgPlans
    ? (billingInterval === 'YEARLY' ? 'credits /seat/yr' : 'credits /seat/mo')
    : (billingInterval === 'YEARLY' ? 'credits/year' : 'credits/month');

  const savings = getSavings();
  const features = activePlan.features || [];

  return (
    <Col xs={24} sm={12} lg={6} key={family.key}>
      <Card
        className={`pricing-card ${hasCurrentPlan ? 'current-plan' : ''} ${isPopular && !hasCurrentPlan ? 'popular-plan' : ''} ${isNitro ? 'nitro-card' : ''}`}
        hoverable
      >
        {/* Popular badge — only when no current plan in this family */}
        {isPopular && !hasCurrentPlan && (
          <div className="plan-badge popular-badge">
            <span>Popular</span>
          </div>
        )}

        {/* Current plan badge — when user's plan is in this family AND slider is at current */}
        {hasCurrentPlan && (
          <div className="plan-badge current-badge">
            <span>✓ Current</span>
          </div>
        )}

        <div className="pricing-card-header">
          <div className={`pricing-card-icon ${isNitro ? 'nitro-icon' : ''}`}>
            <img src={family.icon} alt={`${family.label} plan`} className="plan-tier-icon" />
          </div>
          <h3 className="pricing-card-title">
            {isOrgPlans ? `${family.label} Team` : family.label}
          </h3>
          <div className="slider-plan-subtitle">
            {hasSlider ? activePlan.display_name : '\u00A0'}
          </div>
        </div>

        {/* Slider zone — fixed height for vertical alignment */}
        <div className="pricing-slider-zone">
          {hasSlider && plans.length > 1 ? (
            <Slider
              min={0}
              max={plans.length - 1}
              step={null}
              marks={sliderMarks}
              value={selectedIndex}
              onChange={setSelectedIndex}
              tooltip={{ open: false }}
              className="pricing-tier-slider"
            />
          ) : null}
        </div>

        {/* Dynamic content — credits lead (the value), price follows (the number) */}
        <div className="slider-dynamic-content" key={activePlan.tier}>
          <div className={`pricing-card-credits ${isNitro ? 'nitro-credits' : ''}`}>
            <span className="credits-amount">{credits.toLocaleString()}</span>
            <span className="pricing-card-credits-label">{creditsLabel}</span>
          </div>

          {activePlan.tagline && (
            <div className="pricing-card-tagline">{activePlan.tagline}</div>
          )}

          <div className="pricing-card-price">
            {getPrice()}
            {savings && !isOrgPlans && (
              <div className="pricing-card-savings">
                Save ${savings.amount} ({savings.percent}%)
              </div>
            )}
          </div>

          <List
            className="pricing-card-features"
            dataSource={features}
            renderItem={(feature) => (
              <List.Item>
                <CheckOutlined className="feature-check" />
                <span>{feature}</span>
              </List.Item>
            )}
          />

        </div>

        {/* Status zone — minimal height spacer for button alignment */}
        <div className="slider-status-zone" />

        <Button
          type="primary"
          size="large"
          block
          disabled={loading}
          loading={loadingTier === activePlan.tier}
          onClick={() => {
            setLoadingTier(activePlan.tier);
            onSelect(activePlan.tier, billingInterval, displaySeatCount);
          }}
          className="pricing-card-button"
        >
          {getButtonText()}
        </Button>
      </Card>
    </Col>
  );
};

/* ============================================================
   Main PricingCards Component — Slider Layout Only
   ============================================================ */

const PricingCards = ({
  plans,
  currentTier,
  currentInterval,
  interval,
  onSelect,
  loading,
  seatCount = 1,
  isOrgPlans = false,
  showEnterprise = false,
  isPublicMode = false,
  isPublicModeLoggedIn = false,
  planOptions = null,
  planOptionsLoading = false,
  hasPositiveBalance = false,
  onSeatCountChange = null,
  onIntervalChange = null,
}) => {
  const [billingInterval, setBillingInterval] = useState(interval || 'MONTHLY');
  const [selectedSeatCount, setSelectedSeatCount] = useState(seatCount || 1);
  const [loadingTier, setLoadingTier] = useState(null);

  // Reset loadingTier when parent loading prop changes to false
  React.useEffect(() => {
    if (!loading) {
      setLoadingTier(null);
    }
  }, [loading]);

  // For org plans, use user-selected seat count; for personal, always 1
  const displaySeatCount = isOrgPlans ? selectedSeatCount : 1;

  const handleSeatChange = (val) => {
    const newCount = val || 1;
    setSelectedSeatCount(newCount);
    if (onSeatCountChange) {
      onSeatCountChange(newCount);
    }
  };

  const handleEnterpriseContact = () => {
    window.location.href = '/contact';
  };

  const families = groupPlansByFamily(plans);

  return (
    <div className="pricing-cards-container">
      <div className="pricing-interval-toggle">
        <span className={billingInterval === 'MONTHLY' ? 'active' : ''}>Monthly</span>
        <Switch
          checked={billingInterval === 'YEARLY'}
          onChange={(checked) => {
            const iv = checked ? 'YEARLY' : 'MONTHLY';
            setBillingInterval(iv);
            if (onIntervalChange) onIntervalChange(iv);
          }}
          className="pricing-toggle-switch"
        />
        <span className={billingInterval === 'YEARLY' ? 'active' : ''}>
          Yearly
          <span className="pricing-savings-badge">
            Save ~{plans.length > 0 ? Math.round((1 - plans[0].yearly_price / (plans[0].monthly_price * 12)) * 100) : 8}%
          </span>
        </span>
      </div>

      {/* Seat selector — only for org plans */}
      {isOrgPlans && (
        <div className="pricing-seat-selector">
          <label className="seat-selector-label">Team size:</label>
          <InputNumber
            min={1}
            value={selectedSeatCount}
            onChange={handleSeatChange}
            className="seat-selector-input"
          />
          <span className="seat-selector-suffix">
            {selectedSeatCount === 1 ? 'seat' : 'seats'}
          </span>
        </div>
      )}

      <Row gutter={[24, 24]} className="pricing-cards-row pricing-slider-row">
        {families.map((family) => (
          <SliderFamilyCard
            key={family.key}
            family={family}
            billingInterval={billingInterval}
            onSelect={onSelect}
            loading={loading}
            loadingTier={loadingTier}
            setLoadingTier={setLoadingTier}
            isPublicMode={isPublicMode}
            isPublicModeLoggedIn={isPublicModeLoggedIn}
            isOrgPlans={isOrgPlans}
            displaySeatCount={displaySeatCount}
            currentTier={currentTier}
            hasPositiveBalance={hasPositiveBalance}
          />
        ))}

        {/* Enterprise Card - only show when explicitly enabled */}
        {showEnterprise && (
          <Col xs={24} sm={12} lg={6}>
            <Card className="pricing-card enterprise-card" hoverable>
              <div className="pricing-card-header">
                <div className="pricing-card-icon enterprise-icon">
                  <img src="/icons/plan_enterprise.svg" alt="Enterprise plan" className="plan-tier-icon" />
                </div>
                <h3 className="pricing-card-title">Enterprise</h3>
              </div>

              <div className="pricing-card-price">
                <div className="pricing-card-price-label">
                  <div className="price-main">Custom</div>
                  <div className="price-subtitle">tailored to your needs</div>
                </div>
              </div>

              <div className="pricing-card-credits enterprise-credits">
                <span className="credits-amount">Unlimited</span>
                <span className="pricing-card-credits-label">credits available</span>
              </div>

              <List
                className="pricing-card-features"
                dataSource={[
                  'Unlimited credits',
                  'Dedicated account manager',
                  'Priority SLA',
                  'Volume discounts',
                  'Custom billing terms',
                ]}
                renderItem={(feature) => (
                  <List.Item>
                    <CheckOutlined className="feature-check" />
                    <span>{feature}</span>
                  </List.Item>
                )}
              />

              <Button
                type="default"
                size="large"
                block
                onClick={handleEnterpriseContact}
                className="pricing-card-button enterprise-button"
                icon={<MailOutlined />}
              >
                Contact Sales
              </Button>

              <div className="enterprise-email">Replies within 1 business day</div>
            </Card>
          </Col>
        )}
      </Row>

      {/* Credit expiry benefit */}
      <div className="pricing-credits-expiry-benefit">
        <span className="expiry-benefit-icon">✨</span>
        <span>Credits stay valid for 60 days after your billing period — no rush to use them.</span>
      </div>
    </div>
  );
};

export default PricingCards;
